import {
  ConversationContext,
  ConversationMessage,
  ConversationParticipant,
  StartConversationParams,
} from '@stripe-internal/conversations-client';
import {ConversationsApiDataMessage} from '../generated/types';
import {ApiResponse, ReportWarning} from './ApiMethods';
import {FrameworkAdapter, PollInputModes} from './FrameworkAdapter';

type PollConversationDeps = {
  pollConversation: (
    conversationId: string,
    messageIds?: string[],
  ) => ApiResponse<ConversationsApiDataMessage[]>;
  attachmentDownloadPath?: string;
  reportWarning?: ReportWarning;
  // Threads-only: the backend performs live-support handoffs and declares
  // them in poll responses, which Threads settles from context. Leave unset
  // for consumers whose client still drives transitions from send responses.
  includeTransitionContext?: boolean;
};

type PollConversationParams = {
  conversationId: string;
  participantsById: Record<string, ConversationParticipant>;
  user?: NonNullable<StartConversationParams['participant']>;
  dynamicMessageIds?: string[];
};

type PollConversationResult =
  | {
      type: 'success';
      messages: Record<string, ConversationMessage>;
      inputModes?: PollInputModes;
      context?: ConversationContext;
      // todo(@danielxu): temporary — remove once the experiment is over.
      temporaryExperimentVariant?: string;
    }
  | {type: 'error'; error: string; code: number};

export class PollConversationHandler {
  private _deps: PollConversationDeps;

  constructor(deps: PollConversationDeps) {
    this._deps = deps;
  }

  async call(params: PollConversationParams): Promise<PollConversationResult> {
    const {conversationId, participantsById, user, dynamicMessageIds} = params;

    if (!conversationId) {
      return {type: 'error', error: 'Missing conversationId', code: 400};
    }

    const response = await this._deps.pollConversation(
      conversationId,
      dynamicMessageIds,
    );

    if (response.type === 'error') {
      return {type: 'error', error: response.error, code: response.code};
    }

    const messages = FrameworkAdapter.fromApiMessages(
      conversationId,
      response.data,
      participantsById,
      user,
      this._deps.attachmentDownloadPath,
      this._deps.reportWarning,
    );

    const inputModes = FrameworkAdapter.pollInputModesFromApiMessages(
      response.data,
    );

    const temporaryExperimentVariant = response.data
      .map((m) => m.temporary_experiment_variant)
      .filter((v): v is string => !!v)
      .at(-1);

    const context = this._deps.includeTransitionContext
      ? FrameworkAdapter.deriveChannelContext(response.data)
      : {};
    return {
      type: 'success',
      messages,
      inputModes,
      temporaryExperimentVariant,
      ...(context.channel ? {context} : {}),
    };
  }
}
